import * as React from "react";
import { handleDragOver, handleDragLeave, handleFileDrop } from "./utils/dragAndDrop";
import { validateImageFile } from "./utils/photoHelpers";

function MultiPhotoSlot({
    template,
    uploadedPhotos,
    existingPhotos,
    deletingPhotos,
    updatingPhotos,
    downloadingPhotos,
    onFileSelect,
    onRemovePhoto,
    onDeleteExisting,
    onUpdateExisting,
    onDownload
}) {
    const [pendingFile, setPendingFile] = React.useState(null);
    const [pendingSubTag, setPendingSubTag] = React.useState(null);
    const fileInputRef = React.useRef(null);

    const stagedEntries = React.useMemo(() => {
        return template.subTags
            .map(subTag => {
                const key = `${template.name}::${subTag}`;
                const photo = uploadedPhotos?.[key];
                return photo ? { subTag, key, photo } : null;
            })
            .filter(Boolean);
    }, [template, uploadedPhotos]);

    const existingEntries = React.useMemo(() => {
        return template.subTags
            .map(subTag => {
                const ex = (existingPhotos || []).find(
                    p => p.photo_name === template.name && (p.sub_tag || null) === subTag
                );
                return ex ? { subTag, photo: ex } : null;
            })
            .filter(Boolean);
    }, [template, existingPhotos]);

    const takenSubTags = React.useMemo(() => {
        const taken = new Set();
        stagedEntries.forEach(e => taken.add(e.subTag));
        existingEntries.forEach(e => taken.add(e.subTag));
        return taken;
    }, [stagedEntries, existingEntries]);

    const anyFilled = stagedEntries.length > 0 || existingEntries.length > 0;

    const handleDrop = (e) => {
        const file = handleFileDrop(e);
        if (!file) return;
        const validation = validateImageFile(file);
        if (!validation.valid) { alert(validation.error); return; }
        const reader = new FileReader();
        reader.onload = ev => setPendingFile({ file, preview: ev.target.result });
        reader.readAsDataURL(file);
        setPendingSubTag(null);
    };

    const handleFileInputChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        e.target.value = '';
        const validation = validateImageFile(file);
        if (!validation.valid) { alert(validation.error); return; }
        const reader = new FileReader();
        reader.onload = ev => setPendingFile({ file, preview: ev.target.result });
        reader.readAsDataURL(file);
        setPendingSubTag(null);
    };

    const confirmPending = () => {
        if (!pendingFile || !pendingSubTag) return;
        onFileSelect(pendingFile.file, template, pendingSubTag);
        setPendingFile(null);
        setPendingSubTag(null);
    };

    const cancelPending = () => {
        setPendingFile(null);
        setPendingSubTag(null);
    };

    return (
        <div className={`photo-slot photo-slot-multi${anyFilled ? ' filled' : ''}`}>
            <div className="photo-slot-header">
                <div className="title-direction">
                    <h4>{template.name}</h4>
                    {template.icon && <span className="direction-icon"><template.icon /></span>}
                </div>
                <p className="photo-description">{template.description}</p>
            </div>

            <div className="photo-slot-body multi-body">
                {existingEntries.map(({ subTag, photo }) => {
                    const isDeleting = deletingPhotos?.[photo.file_id];
                    const isUpdating = updatingPhotos?.[photo.file_id];
                    const isDownloading = downloadingPhotos?.[photo.file_id];
                    return (
                        <div key={subTag} className="multi-photo-row existing-row">
                            <img
                                src={photo.thumbnail_url || photo.square_url}
                                alt={subTag}
                                className="multi-thumb"
                            />
                            <span className="multi-row-label">{subTag}</span>
                            <span className="existing-badge multi-badge">Saved</span>
                            <div className="multi-row-actions">
                                <button
                                    className="multi-action-btn download"
                                    onClick={() => onDownload(photo)}
                                    disabled={isDownloading || isDeleting || isUpdating}
                                >{isDownloading ? 'Downloading...' : 'Download'}</button>
                                <label className={`multi-action-btn replace${isUpdating || isDeleting || isDownloading ? ' disabled' : ''}`}>
                                    <input
                                        type="file"
                                        name={`replace-${photo.file_id}`}
                                        accept="image/*"
                                        className="file-input"
                                        disabled={isUpdating || isDeleting || isDownloading}
                                        onChange={async (e) => {
                                            const file = e.target.files[0];
                                            if (!file) return;
                                            e.target.value = '';
                                            const v = validateImageFile(file);
                                            if (!v.valid) { alert(v.error); return; }
                                            if (!window.confirm(`Replace "${photo.photo_name} — ${subTag}"?`)) return;
                                            onUpdateExisting(file, photo, template);
                                        }}
                                    />
                                    {isUpdating ? 'Updating...' : 'Replace'}
                                </label>
                                <button
                                    className="multi-action-btn delete"
                                    onClick={() => onDeleteExisting(photo.file_id, photo.photo_name)}
                                    disabled={isDeleting || isUpdating || isDownloading}
                                >{isDeleting ? 'Deleting...' : 'Delete'}</button>
                            </div>
                        </div>
                    );
                })}

                {stagedEntries.map(({ subTag, key, photo }) => (
                    <div key={key} className="multi-photo-row staged-row">
                        <img src={photo.preview} alt={subTag} className="multi-thumb" />
                        <span className="multi-row-label">{subTag}</span>
                        <span className="multi-staged-badge">Queued</span>
                        <button
                            className="multi-action-btn delete"
                            onClick={() => onRemovePhoto(key)}
                        >Remove</button>
                    </div>
                ))}

                {pendingFile && (
                    <div className="multi-pending">
                        <img src={pendingFile.preview} alt="pending" className="multi-pending-thumb" />
                        <div className="multi-pending-right">
                            <p className="multi-pending-label">Select photo type:</p>
                            <div className="multi-subtag-list">
                                {template.subTags.map(subTag => {
                                    const taken = takenSubTags.has(subTag);
                                    return (
                                        <label
                                            key={subTag}
                                            className={`multi-subtag-option${pendingSubTag === subTag ? ' selected' : ''}${taken ? ' taken' : ''}`}
                                        >
                                            <input
                                                type="radio"
                                                name={`subtag-${template.name}`}
                                                value={subTag}
                                                disabled={taken}
                                                checked={pendingSubTag === subTag}
                                                onChange={() => setPendingSubTag(subTag)}
                                            />
                                            {subTag}
                                            {taken && <span className="multi-subtag-taken"> (filled)</span>}
                                        </label>
                                    );
                                })}
                            </div>
                            <div className="multi-pending-actions">
                                <button
                                    className="multi-confirm-btn"
                                    onClick={confirmPending}
                                    disabled={!pendingSubTag}
                                >Confirm</button>
                                <button className="multi-cancel-btn" onClick={cancelPending}>Cancel</button>
                            </div>
                        </div>
                    </div>
                )}

                {!pendingFile && (
                    <div
                        className="photo-upload-placeholder multi-dropzone"
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        <label className="upload-label">
                            <input
                                ref={fileInputRef}
                                type="file"
                                name={`multi-upload-${template.name}`}
                                accept="image/*"
                                onChange={handleFileInputChange}
                                className="file-input"
                            />
                            <span className="upload-icon">+</span>
                            <span className="upload-text">
                                {anyFilled ? 'Add another photo' : 'Choose Photo'}
                            </span>
                            <span className="drag-text">or drag and drop</span>
                        </label>
                    </div>
                )}
            </div>
        </div>
    );
}

function PhotoSlot({
    template,
    uploadedPhoto,
    existingPhoto,
    isDeleting,
    isUpdating,
    isDownloading,
    uploadedPhotos,
    existingPhotos,
    deletingPhotos,
    updatingPhotos,
    downloadingPhotos,
    onFileSelect,
    onRemovePhoto,
    onDeleteExisting,
    onUpdateExisting,
    onDropForUpdate,
    onDownload
}) {
    if (template.multiPhoto && template.subTags) {
        return (
            <MultiPhotoSlot
                template={template}
                uploadedPhotos={uploadedPhotos}
                existingPhotos={existingPhotos}
                deletingPhotos={deletingPhotos}
                updatingPhotos={updatingPhotos}
                downloadingPhotos={downloadingPhotos}
                onFileSelect={onFileSelect}
                onRemovePhoto={onRemovePhoto}
                onDeleteExisting={onDeleteExisting}
                onUpdateExisting={onUpdateExisting}
                onDownload={onDownload}
            />
        );
    }

    const handleDrop = (e) => {
        const file = handleFileDrop(e);
        if (file) {
            onFileSelect(file, template);
        }
    };

    const handleDropUpdate = async (e) => {
        const file = handleFileDrop(e);
        if (!file) return;

        const validation = validateImageFile(file);
        if (!validation.valid) {
            alert(validation.error);
            return;
        }

        if (!window.confirm(`Replace "${existingPhoto.photo_name}" with new image?`)) {
            return;
        }

        onDropForUpdate(file, existingPhoto, template);
    };

    const handleFileInputChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            onFileSelect(file, template);
        }
    };

    const handleUpdateFileInputChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const validation = validateImageFile(file);
        if (!validation.valid) {
            alert(validation.error);
            return;
        }

        if (!window.confirm(`Replace "${existingPhoto.photo_name}" with new image?`)) {
            return;
        }

        onUpdateExisting(file, existingPhoto, template);
    };

    return (
        <div className={`photo-slot ${uploadedPhoto || existingPhoto ? 'filled' : ''}`}>
            <div className="photo-slot-header">
                <div className="title-direction">
                    <h4>{template.name}</h4>
                    {template.icon && <span className="direction-icon"><template.icon /></span>}
                </div>
                <p className="photo-description">{template.description}</p>
            </div>
            <div className="photo-slot-body">
                {uploadedPhoto ? (
                    <div className="photo-preview">
                        <img src={uploadedPhoto.preview} alt={template.name} />
                        <button
                            className="remove-photo-btn"
                            onClick={() => onRemovePhoto(template.name)}
                        >
                            Remove
                        </button>
                    </div>
                ) : existingPhoto ? (
                    <div
                        className="photo-preview existing"
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDropUpdate}
                    >
                        <img src={existingPhoto.thumbnail_url || existingPhoto.square_url} alt={template.name} />
                        <div className="existing-photo-overlay">
                            <span className="existing-badge">Existing</span>
                        </div>
                        <div className="existing-photo-actions">
                            <button
                                className="download-photo-btn"
                                onClick={() => onDownload(existingPhoto)}
                                disabled={isDownloading || isUpdating || isDeleting}
                            >
                                {isDownloading ? 'Downloading...' : 'Download'}
                            </button>
                            <label className="update-photo-btn" disabled={isUpdating || isDeleting || isDownloading}>
                                <input
                                    type="file"
                                    name={`replace-${template.name}`}
                                    accept="image/*"
                                    onChange={handleUpdateFileInputChange}
                                    className="file-input"
                                    disabled={isUpdating || isDeleting || isDownloading}
                                />
                                {isUpdating ? 'Updating...' : 'Replace'}
                            </label>
                            <button
                                className="delete-photo-btn"
                                onClick={() => onDeleteExisting(existingPhoto.file_id, existingPhoto.photo_name)}
                                disabled={isDeleting || isUpdating || isDownloading}
                            >
                                {isDeleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div
                        className="photo-upload-placeholder"
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        <label className="upload-label">
                            <input
                                type="file"
                                name={`upload-${template.name}`}
                                accept="image/*"
                                onChange={handleFileInputChange}
                                className="file-input"
                            />
                            <span className="upload-icon">+</span>
                            <span className="upload-text">Choose Photo</span>
                            <span className="drag-text">or drag and drop</span>
                        </label>
                    </div>
                )}
            </div>
        </div>
    );
}

export default PhotoSlot;
